import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import DashboardContext from './context';
import Hero from '../common/hero';
import Loader from '../common/loader';
import Actions from '../common/actions';
import Filters from '../common/filters';
import NewAccountInsights from './new-account-insights';
import NewPublishedWires from './new-published-wires';
import getWiresStats from '../../../services/analytics/published-wires-service';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  ${props => props.theme.isXXLarge && `
    width: 1200px;
  `};
  ${props => props.theme.isXLarge && `
    width: 953px;
  `};
  ${props => props.theme.isLarge && `
    width: 85%;
  `};
  ${props => props.theme.isMobile && `
    width: 100%;
  `};
`;

const contributorsToMaxDaysAgo = contributors => {
  const endOfDay = moment().endOf('day');
  const createdDays = contributors.map(c => endOfDay.diff(c.createdAt, 'days'));
  return Math.max.apply(null, createdDays);
};

export default function NewDesktopView({ partnership }) {
  const { id, cachedAnalyticsDataUrls, contributors } = partnership;
  const cachedPerformanceDataUrl = cachedAnalyticsDataUrls.performanceDataFromDatabaseUrl;
  const { state, dispatch } = React.useContext(DashboardContext);
  const [loading, setLoading] = React.useState(false);
  const [inited, setInited] = React.useState(false);
  const [token, setToken] = React.useState(null);

  const fetchWires = React.useCallback(fromCache => {
    if (!_.get(contributors, 'length')) {
      return;
    }
    const actualDaysAgo = contributorsToMaxDaysAgo(contributors);

    setLoading(true);
    const [task, source] = getWiresStats(
      id,
      contributors.map(c => c.id),
      actualDaysAgo,
      cachedPerformanceDataUrl,
      fromCache,
    );
    setToken(source);
    task
      .then(response => {
        const payload = _.get(response, 'data', []);
        if (payload.length) {
          setInited(true);
          dispatch({ type: 'setRawWires', payload });
          dispatch({ type: 'setGeneratedAt', payload: moment() });
        }
      })
      .finally(() => {
        dispatch({ type: 'setRequestData', payload: false });
        setLoading(false);
        setToken(null);
      });
  }, [contributors, id, dispatch, cachedPerformanceDataUrl]);

  React.useEffect(() => {
    inited || fetchWires(true);
  }, [inited, fetchWires]);

  React.useEffect(() => {
    state.requestData && fetchWires(false);
  }, [state.requestData, fetchWires]);

  React.useEffect(() => {
    let tid;
    if (loading) {
      tid = setTimeout(() => dispatch({ type: 'setLoading', payload: true }), 200);
    } else {
      dispatch({ type: 'setLoading', payload: false });
    }
    return () => clearTimeout(tid);
  }, [loading, dispatch]);

  const onCancel = () => token && token.cancel();

  const filtersProps = {
    contributors: state.contributors,
    daysAgo: state.daysAgo,
    onContributorsChange: payload => dispatch({ type: 'setContributors', payload }),
    onDateChange: payload => dispatch({ type: 'setDaysAgo', payload }),
  };

  const actionsProps = {
    generatedAt: state.generatedAt,
    onRefreshClick: () => dispatch({ type: 'setRequestData', payload: true }),
  };

  return (
    <>
      <Hero type="performance" />
      <Container>
        {!!state.loading && <Loader cancellable={inited} onCancel={onCancel} />}
        <Filters {...filtersProps} />
        <Actions {...actionsProps} />
        <NewAccountInsights />
        <NewPublishedWires />
      </Container>
    </>
  );
}

NewDesktopView.propTypes = {
  partnership: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    cachedAnalyticsDataUrls: PropTypes.shape({
      performanceDataFromDatabaseUrl: PropTypes.string,
    }),
    contributors: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      profilePictureUrl: PropTypes.string,
      createdAt: PropTypes.string,
    })).isRequired,
  }).isRequired,
};
